import React, { useEffect, useRef, useState } from "react";
import { Form, Spin, Select, InputNumber, Modal, DatePicker } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { save } from "../../../../../../../features/save/saveSlice";
import { normalizeDateToLocalYear } from "../../../../../../../utils";

const DonorAdd = ({ isModalOpen, handleCancel, handleFetch, prodd }) => {
  const urlSymbol = "api/v2/moneySymbol/get";
  const urlDonor = "api/v2/irrPart/fetch";
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const fundingType = Form.useWatch("pdFundingType", form);
  const { saving } = useSelector((state) => state.save);
  const { dataSymbol, activeObjOngoingProject, dataDonor } = useSelector(
    (state) => ({
      dataSymbol: state.fetch.data[urlSymbol] || [],
      dataDonor: state.fetch.data[urlDonor] || [],
      activeObjOngoingProject: state.obj.activeObj["ongoingProject"],
    })
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onFinish = async (data) => {
    data.pdId = prodd?.pdId ? prodd?.pdId : null;
    data.pdProjectId = activeObjOngoingProject?.projectId;
    data.pdStartDate = normalizeDateToLocalYear(data?.pdStartDate);
    data.pdEndDate = normalizeDateToLocalYear(data?.pdEndDate);
    const res = await dispatch(
      save({
        ...data,
        url: "api/v2/mainProject/donor/save",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await handleFetch();
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const onValuesChange = (changedValues, allValues) => {
    const { pdFundingType, pdGrantAmount, pdLoanAmount, pdExchequerAmount, pdConversionRate } = allValues;

    let pdAmount = allValues.pdAmount;

    // Clear amount fields when funding type changes
    if (changedValues.pdFundingType !== undefined) {
      form.setFieldsValue({
        pdGrantAmount: undefined,
        pdLoanAmount: undefined,
        pdExchequerAmount: undefined,
        pdAmount: undefined,
        pdConvertedAmount: undefined,
      });
      return;
    }

    if (changedValues.pdGrantAmount !== undefined || changedValues.pdLoanAmount !== undefined) {
      if (pdFundingType === "GRANT_LOAN") {
        pdAmount = (pdGrantAmount || 0) + (pdLoanAmount || 0);
        form.setFieldsValue({ pdAmount });
      }
    }

    if (changedValues.pdExchequerAmount !== undefined) {
      if (pdFundingType === "EXCHEQUER") {
        pdAmount = pdExchequerAmount || 0;
        form.setFieldsValue({ pdAmount });
      }
    }

    if (pdAmount && pdConversionRate) {
      const converted = pdAmount * pdConversionRate;
      form.setFieldsValue({ pdConvertedAmount: converted });
    }
  };

  useEffect(() => {
    if (prodd) {
      form.setFieldsValue({
        ...prodd,
        pdStartDate: prodd?.pdStartDate
          ? dayjs(new Date(prodd?.pdStartDate))
          : null,
        pdEndDate: prodd?.pdEndDate ? dayjs(new Date(prodd?.pdEndDate)) : null,
      });
    } else {
      form.resetFields();
    }
  }, [prodd, form]);

  return (
    <Modal
      title={`${prodd?.pdId ? "Update Development Partner" : "Add Development Partner"}`}
      open={isModalOpen}
      onOk={onFinish}
      onCancel={handleCancel}
      className="!w-[59.5rem]"
      maskClosable={false}
    >
      <Form
        layout="vertical"
        ref={formRef}
        name="control-ref"
        onFinish={onFinish}
        className=""
        style={{
          maxWidth: "100%",
        }}
        form={form}
        onValuesChange={onValuesChange}
      >
        <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
          <Form.Item
            name="pdIpId"
            className=""
            label={"Select Development Partner"}
            rules={[
              {
                required: false,
                message: "Please select development partner",
              },
            ]}
          >
            <Select
              className=""
              allowClear
              style={{
                width: "100%",
              }}
              options={
                dataDonor?.length > 0 &&
                dataDonor?.map((item) => ({
                  value: item?.ipId,
                  label: item?.mipName,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase()
                )
              }
            />
          </Form.Item>

          <Form.Item
            name="pdCurrencyId"
            className=""
            label={"Currency"}
            rules={[
              {
                required: true,
                message: "Please select currency",
              },
            ]}
          >
            <Select
              className=""
              allowClear
              style={{
                width: "100%",
              }}
              options={
                dataSymbol?.length > 0 &&
                dataSymbol?.map((item) => ({
                  value: item?.moneySybId,
                  label: item?.moneySybCode,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase()
                )
              }
            />
          </Form.Item>

          <Form.Item
            name="pdFundingType"
            className=""
            label={"Funding Type"}
            rules={[
              {
                required: true,
                message: "Please select funding type",
              },
            ]}
          >
            <Select
              className=""
              allowClear
              style={{
                width: "100%",
              }}
              options={[
                { value: "GRANT_LOAN", label: "Grant/Loan" },
                { value: "EXCHEQUER", label: "Exchequer" },
              ]}
            />
          </Form.Item>

          {fundingType === "GRANT_LOAN" && (
            <>
              <Form.Item
                extra={"You can have it as 0"}
                name="pdGrantAmount"
                label={"Grant Amount"}
                rules={[
                  {
                    required: true,
                    message: "Please add grant amount",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                extra={"You can have it as 0"}
                name="pdLoanAmount"
                label={"Loan Amount"}
                rules={[
                  {
                    required: true,
                    message: "Please add loan amount",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
            </>
          )}

          {fundingType === "EXCHEQUER" && (
            <Form.Item 
              name="pdExchequerAmount"
              label={"Exchequer Amount"}
              rules={[
                {
                  required: true,
                  message: "Please add exchequer amount",
                },
              ]}
            >
              <InputNumber
                min={0}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                className="input flex"
              />
            </Form.Item>
          )}

          <Form.Item
            name="pdAmount"
            label={"Amount"}
            rules={[
              {
                required: true,
                message: "Please add amount",
              },
            ]}
          >
            <InputNumber
              disabled
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="pdConversionRate"
            label={"Current Convertion Rate"}
            rules={[
              {
                required: true,
                message: "Please add convertion Rate",
              },
              {
                validator: (_, value) =>
                  value > 0
                    ? Promise.resolve()
                    : Promise.reject("Conversion rate must be greater than 0"),
              },
            ]}
          >
            <InputNumber
              min={0.01}  
              step={0.01} 
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              className="input flex"
            />
          </Form.Item>

          <Form.Item
            name="pdConvertedAmount"
            label={"Converted Amount"}
            rules={[
              {
                required: true,
                message: "Please add Converted Amount",
              },
            ]}
          >
            <InputNumber
              disabled
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              placeholder="Ksh. e.g 500"
              className="input flex"
            />
          </Form.Item>

          <Form.Item
            name="pdStartDate"
            rules={[
              {
                required: false,
                message: "Start date is required",
              },
            ]}
            label="Start Date"
          >
            <DatePicker format={"YYYY MMM DD"} className="input" />
          </Form.Item>

          <Form.Item
            name="pdEndDate"
            rules={[
              {
                required: true,
                message: "End date is required",
              },
            ]}
            label="End Date"
          >
            <DatePicker format={"YYYY MMM DD"} className="input" />
          </Form.Item>
        </div>

        <div className="flex justify-between mt-[48px] ">
          <div className="justify-start"></div>
          <div className="justify-end flex items-center mb-[58px] gap-x-5">
            <div className="w-[150px]">
              <button
                type="button"
                key="back"
                onClick={handleCancel}
                className="cstm-btn"
              >
                Cancel
              </button>
            </div>

            <div className="w-[180px]">
              <button
                disabled={saving}
                key="submit"
                type="submit"
                className="cstm-btn-2"
              >
                {saving ? (
                  <Spin />
                ) : prodd?.pdId ? (
                  "Update Funder Info"
                ) : (
                  "Save Funder Info"
                )}
              </button>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
export default DonorAdd;
